/**
 * 次に流す曲の音声データを先読みし、blob: URL として渡す
 *
 * 署名付きURLだけ先読みしても、src を差し替えてから音声の取得が始まるため曲の繋ぎ目が無音になる。
 * iOS のバックグラウンド再生のため audio 要素は1つのまま使い回したいので、2つ目の要素ではなく Blob で持つ。
 */

import type { StemVariant } from './music-stem-plan'

type Entry = { controller: AbortController; objectUrl: string | null }

/** これより大きい曲はメモリを圧迫するので先読みせず、従来どおりネットワークから流す */
const MAX_PREFETCH_BYTES = 150 * 1024 * 1024

const entries = new Map<string, Entry>()

export const prefetchKey = (episodeId: number, stem: StemVariant | null): string => `${episodeId}:${stem ?? 'original'}`

const drop = (key: string): void => {
  const entry = entries.get(key)
  if (!entry) return
  entries.delete(key)
  entry.controller.abort()
  if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl)
}

const fetchAudio = async (url: string, signal: AbortSignal): Promise<Blob | null> => {
  try {
    const res = await fetch(url, { signal })
    if (!res.ok) return null
    if (Number(res.headers.get('Content-Length')) > MAX_PREFETCH_BYTES) {
      await res.body?.cancel()
      return null
    }
    return await res.blob()
  } catch (err) {
    if (!signal.aborted) console.warn('[MusicAudio] 次の曲の先読みに失敗しました:', err)
    return null
  }
}

/**
 * key の音声データを先読みする。inUse（再生中の blob: URL）以外の古い先読みは破棄する。
 * 失敗した場合もエントリは残し、timeupdate のたびに取り直さないようにする。
 */
export const prefetchAudio = (key: string, url: string, inUse: string | null): void => {
  for (const [k, entry] of entries) {
    if (k === key) continue
    if (inUse && entry.objectUrl === inUse) continue
    drop(k)
  }
  if (entries.has(key)) return

  const entry: Entry = { controller: new AbortController(), objectUrl: null }
  entries.set(key, entry)
  void fetchAudio(url, entry.controller.signal).then((blob) => {
    if (!blob || entries.get(key) !== entry) return
    entry.objectUrl = URL.createObjectURL(blob)
  })
}

/**
 * 読み込み済みなら blob: URL を返す。
 * 読み込み途中のものはネットワーク再生と帯域を奪い合うので破棄して null を返す。
 */
export const takePrefetchedAudio = (key: string): string | null => {
  const objectUrl = entries.get(key)?.objectUrl ?? null
  if (!objectUrl) drop(key)
  return objectUrl
}

/** 再生に使い終わった blob: URL を解放する */
export const releasePrefetchedAudio = (objectUrl: string): void => {
  for (const [key, entry] of entries) {
    if (entry.objectUrl === objectUrl) drop(key)
  }
}

export const clearPrefetchedAudio = (): void => {
  for (const key of [...entries.keys()]) drop(key)
}
